import clsx from "clsx";
import { ForwardedRef, forwardRef, TextareaHTMLAttributes } from "react";
import { twMerge } from "tailwind-merge";

import { getWrapperClassNames, inputClassNames } from "./class-names";
import { getLabelMessageId } from "./utils";
import { Label } from "../label";

const textAreaStyles = "resize-none min-h-20";

export type BaseTextAreaProps = {
  label?: string;
  mandatory?: boolean;
  message?: string;
  error?: boolean;
  className?: string;
  dataTestId?: string;
  textAreaClassName?: string;
} & Pick<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  | "id"
  | "disabled"
  | "name"
  | "title"
  | "placeholder"
  | "rows"
  | "value"
  | "defaultValue"
  | "onChange"
  | "onBlur"
  | "readOnly"
>;

const BaseTextArea = forwardRef<HTMLTextAreaElement, BaseTextAreaProps>(
  (
    {
      id,
      error,
      message,
      disabled,
      dataTestId,
      className,
      textAreaClassName,
      ...inputProps
    }: BaseTextAreaProps,
    ref,
  ) => (
    <div
      className={twMerge(
        clsx(getWrapperClassNames({ error, disabled }), className),
      )}
      aria-disabled={disabled}
      data-testid={dataTestId}
    >
      <textarea
        id={id}
        disabled={disabled}
        ref={ref}
        aria-invalid={error}
        aria-errormessage={error ? message : undefined}
        className={clsx(inputClassNames, textAreaStyles, textAreaClassName)}
        {...inputProps}
      />
    </div>
  ),
);

const TextAreaComponent = (
  props: BaseTextAreaProps,
  ref: ForwardedRef<HTMLTextAreaElement>,
) => (
  <Label
    text={props.label}
    mandatory={props.mandatory}
    disabled={props.disabled}
    name={props.id}
    error={!!props.error}
    message={props.message}
    messageId={getLabelMessageId(props)}
  >
    <BaseTextArea {...props} ref={ref} />
  </Label>
);

export const TextArea = forwardRef(TextAreaComponent);
